import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, Sparkles, Users, Monitor, CheckCircle2, Star,
  Brain, GraduationCap, Heart, TrendingUp, ArrowRight, MapPin, Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { testimonialByline } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useServices, useTestimonials } from "@/hooks/usePublicContent";

const FADE = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } } };

const HERO_IMG = "https://images.unsplash.com/photo-1549737221-bef65e2604a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxjaGlsZCUyMHJlYWRpbmclMjBib29rJTIwaGFwcHl8ZW58MHx8fHwxNzgxMDU4NjI2fDA&ixlib=rb-4.1.0&q=85";
const BLOCKS_IMG = "https://images.unsplash.com/photo-1558907353-ceb54f3882ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHx3b29kZW4lMjBsZXR0ZXIlMjBibG9ja3MlMjBzcGVsbGluZ3xlbnwwfHx8fDE3ODEwNTg2MjZ8MA&ixlib=rb-4.1.0&q=85";
const TEACHER_IMG = "https://images.unsplash.com/photo-1583468991267-3f068b607ae1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHx0ZWFjaGVyJTIwaGVscGluZyUyMHN0dWRlbnR8ZW58MHx8fHwxNzgxMDU4NjI2fDA&ixlib=rb-4.1.0&q=85";

const SERVICE_ICONS = { "private-1-1": BookOpen, "small-group": Users, "virtual-1-1": Monitor };

const FAQS = [
  { q: "What ages does Jonna work with?", a: "Jonna specializes in students ages 5–13 (Kindergarten through 8th grade), with deep experience in elementary and middle-school reading intervention." },
  { q: "What is Orton-Gillingham and why does it work?", a: "Orton-Gillingham is a structured, multi-sensory approach to teaching reading proven by decades of research to help kids with dyslexia. It teaches the predictable patterns of English explicitly — sight, sound, and touch together — so the brain builds reliable reading pathways." },
  { q: "How do I know if my child has dyslexia?", a: "Common signs include difficulty sounding out words, mixing up similar letters, slow or labored reading, avoiding reading aloud, trouble with spelling, and reading well below grade-level expectations. An assessment is the surest way — Jonna can screen for signs and refer for full evaluation if needed." },
  { q: "How often should we tutor?", a: "Frequency depends on each child's individual needs. However, 1×/week is recommended for kids supporting current learning or preventing the summer slide, and 2×/week for students who need to close significant gaps." },
  { q: "Do you offer virtual tutoring?", a: "Yes. Jonna delivers the same evidence-based 1:1 instruction live over video, using interactive digital letter tiles and multi-sensory tools. Available to families anywhere in the U.S." },
  { q: "What does a typical session look like?", a: "Each 60-minute session is custom-built from your child's assessment results. It includes phonemic awareness warm-ups, explicit phonics instruction, decoding practice with real text, fluency work, spelling and written instruction, and a check-for-understanding. Sessions are firm but fun — kids leave feeling capable." },
  { q: "Where are you located?", a: "Jonna serves Springboro, OH and surrounding areas in-person, and offers virtual tutoring to families nationwide." },
];

export default function Home() {
  const services = useServices();
  const testimonials = useTestimonials();

  return (
    <main className="bg-cream">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-grain opacity-50" aria-hidden="true" />
        <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-mustard/30 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-24 w-[380px] h-[380px] rounded-full bg-sage/30 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div initial="hidden" animate="show" variants={FADE} className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-sage-100 px-3.5 py-1.5 text-xs sm:text-sm text-sage-600 font-medium">
                <Sparkles size={14} /> Certified Orton-Gillingham · 17+ years experience
              </div>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-ink text-balance">
                Reading help that actually works — for kids who learn differently.
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-ink/70 leading-relaxed">
                I'm Jonna Chrisman, M.Ed. — a Structured Literacy Dyslexia Interventionist who's helped hundreds of kids
                with <span className="text-ink font-medium">dyslexia, ADHD, and reading difficulties</span> close the gap and
                fall in love with books again. Firm but fun. Evidence-based. Always personalized.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to="/contact" data-testid="hero-waitlist-cta">
                  <Button className="rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-12 px-7 text-base shadow-md hover:-translate-y-0.5 transition-all">
                    Join the Waitlist <ArrowRight size={18} className="ml-1.5" />
                  </Button>
                </Link>
                <Link to="/services" data-testid="hero-services-cta">
                  <Button variant="outline" className="rounded-full border-2 border-ink/80 text-ink hover:bg-ink hover:text-cream h-12 px-7 text-base">
                    See Services & Pricing
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-5 text-sm text-ink/60">
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-sage-600" /> IMSE Orton-Gillingham Certified</span>
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-sage-600" /> CERI Structured Literacy Dyslexia Interventionist</span>
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-sage-600" /> LETRS Trained</span>
              </div>
            </motion.div>
            <motion.div initial="hidden" animate="show" variants={FADE} className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-terracotta-100 rounded-3xl rotate-6" aria-hidden="true" />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-mustard-100 rounded-full" aria-hidden="true" />
                <img
                  src={HERO_IMG}
                  alt="Child reading a book and smiling"
                  className="relative w-full h-[460px] object-cover rounded-[2rem] shadow-xl"
                  data-testid="hero-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-ink/5 bg-white" data-testid="trust-strip">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-10">
          <p className="text-center text-xs uppercase tracking-[0.18em] text-ink/50 font-medium">Trusted training & certifications</p>
          <div className="mt-6 flex flex-wrap justify-center items-center gap-x-10 gap-y-4 text-ink/70 text-sm font-medium">
            <span>IMSE Orton-Gillingham</span>
            <span className="text-ink/20">·</span>
            <span>CERI Structured Literacy</span>
            <span className="text-ink/20">·</span>
            <span>LETRS</span>
            <span className="text-ink/20">·</span>
            <span>IDA Member</span>
            <span className="text-ink/20">·</span>
            <span>M.Ed. Intervention Specialist</span>
            <span className="text-ink/20">·</span>
            <span>Ohio K-12 Reading Endorsement</span>
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28" data-testid="about-teaser">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative">
              <img
                src={TEACHER_IMG}
                alt="Jonna helping a student with reading"
                className="w-full h-[480px] object-cover rounded-[2rem] shadow-lg"
                data-testid="about-teaser-image"
              />
              <div className="absolute -bottom-6 -right-6 bg-terracotta text-white rounded-2xl p-5 shadow-lg max-w-[200px]">
                <div className="font-display text-4xl font-bold leading-none">17+</div>
                <div className="mt-1 text-sm">years tutoring kids who learn differently</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Meet Jonna</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-ink text-balance">
              A teacher parents trust. A tutor kids look forward to.
            </h2>
            <p className="mt-6 text-ink/70 leading-relaxed">
              Jonna Chrisman has spent 17+ years as a Title 1 Reading Specialist and Intervention Specialist in Ohio
              public schools, plus a thriving private practice serving kids with dyslexia and ADHD, and other disabilities. She's data-driven
              when planning, warm and firm in delivery, and laser-focused on one thing: making sure your child becomes
              a confident reader.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { icon: GraduationCap, t: "M.Ed. + Reading Endorsement", s: "Intervention Specialist K-12" },
                { icon: Brain, t: "Dyslexia + ADHD experience", s: "Specialized for kids who learn differently" },
                { icon: Heart, t: "Firm but fun", s: "Builds confidence alongside skills" },
              ].map(({ icon: Icon, t, s }) => (
                <div key={t} className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-sage-100 flex items-center justify-center flex-none">
                    <Icon size={18} className="text-sage-600" />
                  </div>
                  <div>
                    <div className="font-medium text-ink text-sm">{t}</div>
                    <div className="text-sm text-ink/60">{s}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/about" className="mt-8 inline-flex items-center gap-1.5 text-terracotta font-medium link-underline" data-testid="about-teaser-link">
              Read Jonna's full story <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white py-20 lg:py-28" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Services</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-ink">
              Tutoring built around your child — not the other way around.
            </h2>
            <p className="mt-5 text-ink/70 leading-relaxed">
              Three flexible ways to work with Jonna. Every session uses the same evidence-based,
              Orton-Gillingham foundation — just delivered the way that fits your family best.
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = SERVICE_ICONS[s.slug] || BookOpen;
              const accents = [
                { ring: "ring-terracotta/20", tag: "bg-terracotta-100 text-terracotta-600", icon: "bg-terracotta text-white" },
                { ring: "ring-sage/20", tag: "bg-sage-100 text-sage-600", icon: "bg-sage text-white" },
                { ring: "ring-mustard/30", tag: "bg-mustard-100 text-mustard-600", icon: "bg-mustard text-white" },
              ][i % 3];
              return (
                <motion.div key={s.id} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={FADE}>
                  <Card className={`h-full rounded-2xl border border-ink/5 bg-white shadow-sm ring-1 ${accents.ring} hover:shadow-md hover:-translate-y-1 transition-all`}>
                    <CardContent className="p-7">
                      <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${accents.icon}`}>
                        <Icon size={20} />
                      </div>
                      <h3 className="mt-5 font-display text-xl font-semibold text-ink">{s.title}</h3>
                      <div className={`mt-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${accents.tag}`}>
                        {s.price_label}
                      </div>
                      <p className="mt-4 text-sm text-ink/70 leading-relaxed">{s.description}</p>
                      <ul className="mt-5 space-y-2.5">
                        {s.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-ink/80">
                            <CheckCircle2 size={16} className="mt-0.5 flex-none text-sage-600" /> {f}
                          </li>
                        ))}
                      </ul>
                      {s.recommended_for && (
                        <div className="mt-6 pt-5 border-t border-ink/5 text-xs text-ink/60">
                          <span className="font-medium text-ink/70">Best for: </span>{s.recommended_for}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link to="/services" data-testid="services-see-all">
              <Button variant="outline" className="rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream h-11 px-6">
                Compare all services →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* APPROACH / METHODOLOGY BENTO */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28" data-testid="approach-section">
        <div className="grid lg:grid-cols-2 gap-12 items-end mb-12">
          <div>
            <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Our Approach</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-ink text-balance">
              Structured, multi-sensory, and rooted in research.
            </h2>
          </div>
          <p className="text-ink/70 leading-relaxed">
            Jonna's approach isn't a one-size curriculum. She combines the gold-standard
            Orton-Gillingham method with the data-driven techniques she's honed over 17+ years
            of school-based intervention. Every plan starts with a careful assessment.
          </p>
        </div>
        <div className="grid lg:grid-cols-6 gap-5 auto-rows-[220px]">
          <div className="lg:col-span-3 lg:row-span-2 rounded-3xl bg-ink text-cream p-8 flex flex-col justify-between relative overflow-hidden">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-mustard">01 · Foundation</div>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold">Orton-Gillingham, the right way.</h3>
              <p className="mt-4 text-cream/70 text-sm leading-relaxed max-w-md">
                A multi-sensory, structured, sequential, cumulative approach built specifically
                for how dyslexic brains learn to decode. Sight, sound, and touch — together.
              </p>
            </div>
            <img src={BLOCKS_IMG} alt="Wooden letter blocks" className="absolute -bottom-12 -right-12 w-72 h-72 object-cover rounded-3xl opacity-90" />
          </div>
          <div className="lg:col-span-3 rounded-3xl bg-sage-100 p-7">
            <div className="text-xs uppercase tracking-[0.18em] text-sage-600">02 · Diagnosis</div>
            <h3 className="mt-2 font-display text-xl font-semibold text-ink">Assessment first, always.</h3>
            <p className="mt-2 text-sm text-ink/70 leading-relaxed">
              We assess decoding, word recognition, fluency, phonics, spelling, morphology,
              phonological awareness, and vocabulary — so we know exactly where the gaps are.
            </p>
          </div>
          <div className="lg:col-span-3 rounded-3xl bg-terracotta-100 p-7">
            <div className="text-xs uppercase tracking-[0.18em] text-terracotta-600">03 · Plan</div>
            <h3 className="mt-2 font-display text-xl font-semibold text-ink">Custom intervention plan.</h3>
            <p className="mt-2 text-sm text-ink/70 leading-relaxed">
              Phonics, fluency, comprehension, vocabulary, spelling — sequenced to your child's
              data, not a generic workbook.
            </p>
          </div>
          <div className="lg:col-span-2 rounded-3xl bg-mustard-100 p-7">
            <div className="text-xs uppercase tracking-[0.18em] text-mustard-600">04 · Practice</div>
            <h3 className="mt-2 font-display text-lg font-semibold text-ink">Firm but fun sessions.</h3>
            <p className="mt-2 text-sm text-ink/70 leading-relaxed">
              Built for kids with ADHD too. High pace, clear expectations, real wins.
            </p>
          </div>
          <div className="lg:col-span-2 rounded-3xl bg-white border border-ink/5 p-7">
            <div className="text-xs uppercase tracking-[0.18em] text-ink/50">05 · Monitor</div>
            <h3 className="mt-2 font-display text-lg font-semibold text-ink">Progress reports parents love.</h3>
            <p className="mt-2 text-sm text-ink/70 leading-relaxed">
              Quantified WPM, accuracy, percentiles, mastery — clear updates, no jargon.
            </p>
          </div>
          <div className="lg:col-span-2 rounded-3xl bg-ink/5 p-7 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-ink/50">06 · Partnership</div>
              <h3 className="mt-2 font-display text-lg font-semibold text-ink">Coordinated with school.</h3>
            </div>
            <p className="mt-2 text-sm text-ink/70 leading-relaxed">
              IEP-aligned goals and classroom recommendations when relevant.
            </p>
          </div>
        </div>
      </section>

      {/* FREQUENCY GUIDANCE */}
      <section className="bg-ink text-cream py-20 lg:py-28" data-testid="frequency-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.18em] text-mustard font-semibold">How often?</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-cream text-balance">
              Two paths, depending on where your child is now.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl bg-cream/5 border border-cream/10 p-8 hover:bg-cream/10 transition">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl bg-sage text-ink flex items-center justify-center">
                  <Calendar size={20} />
                </div>
                <span className="rounded-full bg-sage/20 text-sage-100 px-3 py-1 text-xs font-medium">Maintenance</span>
              </div>
              <h3 className="mt-5 font-display text-2xl font-semibold text-cream">1× per week</h3>
              <p className="mt-3 text-cream/70 text-sm leading-relaxed">
                For kids reading at or near grade level who need consistent support, want to keep
                building skills, or are preventing the dreaded summer slide.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-cream/80">
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-sage mt-0.5 flex-none" /> Maintain hard-won skills</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-sage mt-0.5 flex-none" /> Build vocabulary & fluency</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-sage mt-0.5 flex-none" /> Stop summer regression</li>
              </ul>
            </div>
            <div className="rounded-3xl bg-terracotta/15 border border-terracotta/30 p-8 hover:bg-terracotta/20 transition">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl bg-terracotta text-white flex items-center justify-center">
                  <TrendingUp size={20} />
                </div>
                <span className="rounded-full bg-terracotta/30 text-cream px-3 py-1 text-xs font-medium">Accelerate</span>
              </div>
              <h3 className="mt-5 font-display text-2xl font-semibold text-cream">2× per week</h3>
              <p className="mt-3 text-cream/70 text-sm leading-relaxed">
                For kids who need to close significant gaps. The frequency creates the repetition
                dyslexic readers need to build automatic, accurate decoding.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-cream/80">
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-terracotta mt-0.5 flex-none" /> Close significant reading gaps</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-terracotta mt-0.5 flex-none" /> Faster mastery cycles</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-terracotta mt-0.5 flex-none" /> Recommended for diagnosed dyslexia</li>
              </ul>
            </div>
          </div>
          <p className="mt-10 text-center text-cream/55 text-sm">
            Not sure which fits? Tell Jonna a little about your child on the waitlist form — she'll recommend a plan.
          </p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-28" data-testid="testimonials-section">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Success Stories</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-ink">
            Parents who watched their kids find reading again.
          </h2>
        </div>
        {testimonials.length === 0 ? (
          <p className="mt-10 text-ink/60 italic">Reviews coming soon — be one of the first families to share your story.</p>
        ) : (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <Card key={t.id} className="rounded-2xl border border-ink/5 bg-white shadow-sm hover:shadow-md transition" data-testid={`testimonial-card-${t.id}`}>
                <CardContent className="p-7">
                  <div className="flex gap-0.5 text-mustard">
                    {Array.from({ length: t.rating || 5 }).map((_, i) => (
                      <Star key={i} size={16} fill="currentColor" stroke="none" />
                    ))}
                  </div>
                  <p className="mt-4 text-ink/80 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                  <div className="mt-6 pt-5 border-t border-ink/5">
                    <div className="font-medium text-ink text-sm">{t.parent_name}</div>
                    <div className="text-xs text-ink/60" data-testid={`testimonial-byline-${t.id}`}>{testimonialByline(t)}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 lg:py-28" data-testid="faq-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 lg:px-12">
          <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">FAQ</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-ink">
            Questions parents ask before reaching out.
          </h2>
          <Accordion type="single" collapsible className="mt-10 w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-ink/10">
                <AccordionTrigger className="text-left font-display text-lg font-medium text-ink hover:no-underline py-5" data-testid={`faq-q-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink/70 text-base leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20" data-testid="bottom-cta">
        <div className="rounded-[2rem] bg-terracotta text-white p-10 sm:p-14 lg:p-20 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-mustard/40 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-16 -left-16 w-60 h-60 rounded-full bg-sage/30 blur-3xl" aria-hidden="true" />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-balance">
              Your child deserves to feel like a reader.
            </h2>
            <p className="mt-5 text-white/85 text-lg leading-relaxed">
              Spaces fill quickly each season. Join Jonna's waitlist now — she'll reach out personally to
              talk through your child's needs and recommend a plan.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" data-testid="bottom-waitlist-cta">
                <Button className="rounded-full bg-white text-terracotta hover:bg-cream h-12 px-7 text-base font-semibold shadow-md hover:-translate-y-0.5 transition-all">
                  Join the Waitlist <ArrowRight size={18} className="ml-1.5" />
                </Button>
              </Link>
              <Link to="/about" data-testid="bottom-about-cta">
                <Button variant="outline" className="rounded-full border-2 border-white/70 text-white bg-transparent hover:bg-white/10 h-12 px-7 text-base">
                  Learn about Jonna
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-2 text-sm text-white/75">
              <MapPin size={16} /> Serving Springboro, OH in-person · Virtual nationwide
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
